import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, ExternalLink, Plus, Save, Trash2 } from 'lucide-react';
import { BrandSpinnerRing } from '../../components/SiteBrandedLoader';
import { articlesService, BlogLink, BlogSettingsData } from '../../services/api';
import BlogSectionNav from './BlogSectionNav';
import { apiErrorMessage, blogSiteBaseUrl } from './blogAdmin';

type TextKey = Exclude<keyof BlogSettingsData, 'navLinks' | 'footerLinks' | 'social'>;
type SocialKey = keyof BlogSettingsData['social'];

/** همان الگوی بک‌اند: مسیر داخلی سایت، آدرس کامل، ایمیل یا تلفن */
const LINK_PATTERN = /^(\/(?!\/)|https?:\/\/|mailto:|tel:)/;

interface TextField {
  key: TextKey;
  label: string;
  max: number;
  hint?: string;
  multiline?: boolean;
  link?: boolean;
}

const SECTIONS: Array<{ title: string; description?: string; fields: TextField[] }> = [
  {
    title: 'هدر وبلاگ',
    description: 'هدر اختصاصی صفحه‌های مقالات (جدا از هدر سایت).',
    fields: [
      { key: 'brandName', label: 'نام برند (لوگوی متنی)', max: 60 },
      { key: 'loginLabel', label: 'متن دکمهٔ ورود', max: 40 },
      { key: 'loginHref', label: 'مقصد دکمهٔ ورود', max: 500, link: true, hint: 'کاربرِ واردشده به «/dashboard» می‌رود.' },
      { key: 'mobileMenuLoginLabel', label: 'متن دکمهٔ ورود در منوی موبایل', max: 60 },
    ],
  },
  {
    title: 'صفحهٔ فهرست مقالات',
    fields: [
      { key: 'heroTitle', label: 'عنوان صفحه', max: 120 },
      { key: 'heroSubtitle', label: 'زیرعنوان', max: 300 },
      { key: 'searchPlaceholder', label: 'متن داخل جست‌وجو', max: 120 },
      { key: 'featuredTitle', label: 'عنوان بخش منتخب', max: 80 },
      { key: 'featuredBadge', label: 'برچسب کنار عنوان منتخب (دسکتاپ)', max: 60, hint: 'خالی = نمایش داده نمی‌شود.' },
      { key: 'featuredBadgeMobile', label: 'برچسب کنار عنوان منتخب (موبایل)', max: 60 },
      { key: 'allArticlesTitle', label: 'عنوان بخش همه مقالات', max: 80 },
    ],
  },
  {
    title: 'صفحهٔ مقاله',
    fields: [
      { key: 'tocTitle', label: 'عنوان فهرست مطالب', max: 80 },
      { key: 'videoTitle', label: 'عنوان بخش ویدیو', max: 80 },
      { key: 'tagsTitle', label: 'عنوان برچسب‌ها', max: 80 },
      { key: 'feedbackTitle', label: 'سؤال بازخورد', max: 120 },
      { key: 'feedbackSubtitle', label: 'توضیح بازخورد (دسکتاپ)', max: 200 },
      { key: 'relatedTitle', label: 'عنوان مقالات مرتبط', max: 80 },
      { key: 'relatedMoreLabel', label: 'متن لینک «همه مقالات»', max: 80 },
    ],
  },
  {
    title: 'دیدگاه‌ها',
    fields: [
      { key: 'commentsTitle', label: 'عنوان بخش', max: 80 },
      { key: 'commentsSubtitle', label: 'توضیح کنار عنوان (دسکتاپ)', max: 120 },
      { key: 'commentsSubtitleMobile', label: 'توضیح کنار عنوان (موبایل)', max: 120 },
      { key: 'officialBadge', label: 'نشان پاسخ رسمی (دسکتاپ) / نام تیم در موبایل', max: 60 },
      { key: 'officialBadgeMobile', label: 'نشان پاسخ رسمی (موبایل)', max: 60 },
      { key: 'commentFormTitle', label: 'عنوان فرم', max: 80 },
      { key: 'commentNamePlaceholder', label: 'راهنمای فیلد نام (دسکتاپ)', max: 120 },
      { key: 'commentNamePlaceholderMobile', label: 'راهنمای فیلد نام (موبایل)', max: 120 },
      { key: 'commentTextPlaceholder', label: 'راهنمای متن دیدگاه (دسکتاپ)', max: 200 },
      { key: 'commentTextPlaceholderMobile', label: 'راهنمای متن دیدگاه (موبایل)', max: 200 },
      { key: 'commentNote', label: 'یادداشت زیر فرم', max: 200 },
    ],
  },
  {
    title: 'فوتر وبلاگ',
    fields: [
      { key: 'footerTagline', label: 'شعار (دسکتاپ)', max: 300 },
      { key: 'footerTaglineMobileList', label: 'شعار (موبایل، صفحهٔ فهرست)', max: 300, multiline: true },
      { key: 'footerTaglineMobileDetail', label: 'شعار (موبایل، صفحهٔ مقاله)', max: 300, multiline: true },
      { key: 'copyright', label: 'متن کپی‌رایت', max: 200, hint: '{year} با سال شمسی جاری جایگزین می‌شود.' },
    ],
  },
];

const SOCIAL_FIELDS: Array<{ key: SocialKey; label: string; hint?: string }> = [
  { key: 'telegram', label: 'کانال تلگرام', hint: 'مثلاً https://t.me/…' },
  { key: 'instagram', label: 'صفحهٔ اینستاگرام' },
  { key: 'youtube', label: 'کانال یوتیوب' },
  { key: 'podcast', label: 'پادکست', hint: 'مسیر داخلی مثل /podcasts یا آدرس کامل' },
  { key: 'email', label: 'خبرنامه / ایمیل', hint: 'mailto:… یا آدرس صفحهٔ عضویت' },
  { key: 'rss', label: 'خوراک RSS', hint: 'پیش‌فرض: /api/articles/feed' },
];

const inputClass = 'w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-100';

/** ویرایشگر فهرست لینک‌ها (منوی هدر / لینک‌های فوتر) */
const LinkListEditor: React.FC<{ title: string; hint?: string; links: BlogLink[]; max: number; onChange: (links: BlogLink[]) => void }> = ({ title, hint, links, max, onChange }) => {
  const set = (index: number, patch: Partial<BlogLink>) => onChange(links.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  const move = (index: number, dir: -1 | 1) => {
    const next = [...links];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-black text-gray-900">{title}</h3>
          {hint && <p className="mt-0.5 text-xs leading-6 text-gray-500">{hint}</p>}
        </div>
        <button
          type="button"
          disabled={links.length >= max}
          onClick={() => onChange([...links, { label: '', href: '/' }])}
          className="inline-flex items-center gap-1 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-bold text-violet-800 hover:bg-violet-100 disabled:opacity-50"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          افزودن لینک
        </button>
      </div>
      {links.length === 0 && <p className="rounded-xl border border-dashed border-gray-200 py-4 text-center text-xs text-gray-500">لینکی تعریف نشده است.</p>}
      {links.map((link, index) => {
        const badHref = !!link.href && !LINK_PATTERN.test(link.href.trim());
        return (
          <div key={index} className="grid grid-cols-1 gap-2 rounded-xl border border-gray-100 bg-gray-50/60 p-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto]">
            <input value={link.label} onChange={(e) => set(index, { label: e.target.value })} maxLength={60} placeholder="عنوان" aria-label="عنوان لینک" className={inputClass} />
            <div>
              <input
                value={link.href}
                dir="ltr"
                onChange={(e) => set(index, { href: e.target.value })}
                maxLength={500}
                placeholder="/about"
                aria-label="مقصد لینک"
                className={`${inputClass} ${badHref ? 'border-red-300' : ''}`}
              />
              {badHref && <p className="mt-1 text-[11px] text-red-600">با / یا https:// یا mailto: شروع شود.</p>}
            </div>
            <div className="flex items-start gap-1">
              <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="rounded-lg p-2 text-gray-600 hover:bg-white disabled:opacity-30" title="بالاتر">
                <ArrowUp className="h-4 w-4" aria-hidden />
              </button>
              <button type="button" onClick={() => move(index, 1)} disabled={index === links.length - 1} className="rounded-lg p-2 text-gray-600 hover:bg-white disabled:opacity-30" title="پایین‌تر">
                <ArrowDown className="h-4 w-4" aria-hidden />
              </button>
              <button type="button" onClick={() => onChange(links.filter((_, i) => i !== index))} className="rounded-lg p-2 text-red-600 hover:bg-red-50" title="حذف">
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/** تنظیمات متن‌ها و لینک‌های ثابت وبلاگ (هدر، فوتر، عنوان بخش‌ها) */
const BlogSettings: React.FC = () => {
  const [data, setData] = useState<BlogSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 3000);
  }, []);

  useEffect(() => {
    articlesService
      .getSettings()
      .then(setData)
      .catch((err) => setError(apiErrorMessage(err, 'خطا در دریافت تنظیمات وبلاگ')))
      .finally(() => setLoading(false));
  }, []);

  const update = <K extends keyof BlogSettingsData>(key: K, value: BlogSettingsData[K]) => {
    setData((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
  };

  const validate = (d: BlogSettingsData): string | null => {
    const links = [...d.navLinks, ...d.footerLinks];
    if (links.some((l) => !l.label.trim())) return 'عنوان همهٔ لینک‌ها را وارد کنید.';
    if (links.some((l) => !LINK_PATTERN.test(l.href.trim()))) return 'مقصد لینک‌ها باید با / یا https:// یا mailto: یا tel: شروع شود.';
    if (!LINK_PATTERN.test(d.loginHref.trim())) return 'مقصد دکمهٔ ورود معتبر نیست.';
    const badSocial = SOCIAL_FIELDS.find((f) => d.social[f.key].trim() && !LINK_PATTERN.test(d.social[f.key].trim()));
    if (badSocial) return `آدرس «${badSocial.label}» معتبر نیست.`;
    return null;
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    const problem = validate(data);
    if (problem) {
      setError(problem);
      return;
    }
    setError('');
    setSaving(true);
    try {
      const trimLinks = (links: BlogLink[]) => links.map((l) => ({ label: l.label.trim(), href: l.href.trim() }));
      const social = Object.fromEntries(Object.entries(data.social).map(([k, v]) => [k, v.trim()])) as BlogSettingsData['social'];
      const saved = await articlesService.updateSettings({ ...data, navLinks: trimLinks(data.navLinks), footerLinks: trimLinks(data.footerLinks), social });
      setData(saved);
      setDirty(false);
      showToast('تنظیمات وبلاگ ذخیره شد');
    } catch (err) {
      setError(apiErrorMessage(err, 'ذخیره انجام نشد'));
    } finally {
      setSaving(false);
    }
  };

  const saveButton = (
    <div className="flex items-center gap-2">
      <a
        href={`${blogSiteBaseUrl()}/articles`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
      >
        <ExternalLink className="h-4 w-4" aria-hidden />
        مشاهدهٔ وبلاگ
      </a>
      <button
        type="submit"
        form="blog-settings-form"
        disabled={saving || !dirty}
        className="inline-flex items-center gap-1.5 rounded-xl bg-[#4a2e68] px-4 py-2 text-sm font-bold text-white hover:bg-[#392352] disabled:opacity-50"
      >
        <Save className="h-4 w-4" aria-hidden />
        {saving ? 'در حال ذخیره…' : 'ذخیرهٔ تنظیمات'}
      </button>
    </div>
  );

  return (
    <div className="space-y-5">
      <BlogSectionNav
        title="تنظیمات صفحه‌های وبلاگ"
        description="متن‌ها، لینک‌های هدر و فوتر و شبکه‌های اجتماعی مجله. تغییرات بلافاصله در سایت اعمال می‌شود."
        action={data ? saveButton : undefined}
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <BrandSpinnerRing size="md" />
        </div>
      ) : !data ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-700">{error || 'تنظیمات در دسترس نیست.'}</div>
      ) : (
        <form id="blog-settings-form" onSubmit={save} className="space-y-5">
          {error && (
            <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
              {error}
            </div>
          )}

          {SECTIONS.map((section) => (
            <section key={section.title} className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
              <div>
                <h2 className="text-base font-black text-gray-900">{section.title}</h2>
                {section.description && <p className="mt-0.5 text-xs leading-6 text-gray-500">{section.description}</p>}
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {section.fields.map((f) => (
                  <label key={f.key} className="block space-y-1.5">
                    <span className="text-sm font-bold text-gray-800">{f.label}</span>
                    {f.multiline ? (
                      <textarea value={data[f.key]} onChange={(e) => update(f.key, e.target.value)} maxLength={f.max} rows={2} className={inputClass} />
                    ) : (
                      <input value={data[f.key]} onChange={(e) => update(f.key, e.target.value)} maxLength={f.max} dir={f.link ? 'ltr' : undefined} className={inputClass} />
                    )}
                    {f.hint && <span className="block text-xs leading-6 text-gray-500">{f.hint}</span>}
                  </label>
                ))}
              </div>
              {section.title === 'هدر وبلاگ' && (
                <LinkListEditor
                  title="لینک‌های منوی هدر"
                  hint="لینک صفحهٔ فعلی (مثلاً «مقالات») در هدر پررنگ نمایش داده می‌شود. حداکثر ۸ لینک."
                  links={data.navLinks}
                  max={8}
                  onChange={(links) => update('navLinks', links)}
                />
              )}
              {section.title === 'فوتر وبلاگ' && (
                <LinkListEditor title="لینک‌های فوتر" hint="حداکثر ۱۰ لینک." links={data.footerLinks} max={10} onChange={(links) => update('footerLinks', links)} />
              )}
            </section>
          ))}

          <section className="space-y-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div>
              <h2 className="text-base font-black text-gray-900">شبکه‌ها و کانال‌ها</h2>
              <p className="mt-0.5 text-xs leading-6 text-gray-500">آیکون‌های فوتر به این آدرس‌ها لینک می‌شوند؛ آدرس خالی = آیکون بدون لینک.</p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {SOCIAL_FIELDS.map((f) => (
                <label key={f.key} className="block space-y-1.5">
                  <span className="text-sm font-bold text-gray-800">{f.label}</span>
                  <input
                    value={data.social[f.key]}
                    dir="ltr"
                    maxLength={500}
                    onChange={(e) => update('social', { ...data.social, [f.key]: e.target.value })}
                    className={inputClass}
                  />
                  {f.hint && <span className="block text-xs leading-6 text-gray-500">{f.hint}</span>}
                </label>
              ))}
            </div>
          </section>

          <div className="flex justify-end">{saveButton}</div>
        </form>
      )}

      {toast && (
        <div role="status" className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-bold text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

export default BlogSettings;
